/**
 * Secure logging utility that prevents token exposure
 * Sanitizes sensitive data before logging
 */

interface LogLevel {
  ERROR: number;
  WARN: number;
  INFO: number;
  DEBUG: number;
}

const LOG_LEVELS: LogLevel = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
};

// Set log level based on environment
const getCurrentLogLevel = (): number => {
  if (import.meta.env.PROD) {
    return LOG_LEVELS.WARN; // Production: only warnings and errors
  }
  return LOG_LEVELS.DEBUG; // Development: all logs
};

const currentLogLevel = getCurrentLogLevel();

/**
 * Sensitive data patterns to redact
 */
const SENSITIVE_PATTERNS = [
  /bearer\s+[a-zA-Z0-9\-._~+/]+=*/gi, // Bearer tokens
  /token['":\s]*[a-zA-Z0-9\-._~+/]+=*/gi, // Any token field
  /jwt['":\s]*[a-zA-Z0-9\-._~+/]+=*/gi, // JWT tokens
  /refresh['":\s]*[a-zA-Z0-9\-._~+/]+=*/gi, // Refresh tokens
  /authorization['":\s]*[a-zA-Z0-9\-._~+/]+=*/gi, // Authorization headers
  /password['":\s]*[^,}\s]*/gi, // Passwords
  /secret['":\s]*[^,}\s]*/gi, // Secrets
  /key['":\s]*[a-zA-Z0-9\-._~+/]+=*/gi, // API keys
];

/**
 * Sanitize data by removing sensitive information
 */
const sanitizeData = (data: any): any => {
  if (typeof data === 'string') {
    let sanitized = data;
    SENSITIVE_PATTERNS.forEach(pattern => {
      sanitized = sanitized.replace(pattern, (match) => {
        const prefix = match.split(/[:\s]/)[0];
        return `${prefix}: [REDACTED]`;
      });
    });
    return sanitized;
  }

  if (typeof data === 'object' && data !== null) {
    if (Array.isArray(data)) {
      return data.map(item => sanitizeData(item));
    }

    const sanitized: any = {};
    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();
      
      // Redact sensitive keys
      if (lowerKey.includes('token') || 
          lowerKey.includes('password') || 
          lowerKey.includes('secret') || 
          lowerKey.includes('key') ||
          lowerKey.includes('authorization')) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = sanitizeData(value);
      }
    }
    return sanitized;
  }

  return data;
};

/**
 * Format log message with timestamp and context
 */
const formatMessage = (level: string, message: string, context?: string): string => {
  const timestamp = new Date().toISOString();
  const contextStr = context ? ` [${context}]` : '';
  return `[${timestamp}] [${level}]${contextStr} ${message}`;
};

/**
 * Secure logger class
 */
class SecureLogger {
  private context?: string;

  constructor(context?: string) {
    this.context = context;
  }

  /**
   * Create a logger with specific context
   */
  static withContext(context: string): SecureLogger {
    return new SecureLogger(context);
  }

  /**
   * Log error messages
   */
  error(message: string, ...args: any[]): void {
    if (currentLogLevel >= LOG_LEVELS.ERROR) {
      const sanitizedArgs = args.map(arg => sanitizeData(arg));
      console.error(formatMessage('ERROR', message, this.context), ...sanitizedArgs);
    }
  }

  /**
   * Log warning messages
   */
  warn(message: string, ...args: any[]): void {
    if (currentLogLevel >= LOG_LEVELS.WARN) {
      const sanitizedArgs = args.map(arg => sanitizeData(arg));
      console.warn(formatMessage('WARN', message, this.context), ...sanitizedArgs);
    }
  }

  /**
   * Log info messages
   */
  info(message: string, ...args: any[]): void {
    if (currentLogLevel >= LOG_LEVELS.INFO) {
      const sanitizedArgs = args.map(arg => sanitizeData(arg));
      console.log(formatMessage('INFO', message, this.context), ...sanitizedArgs);
    }
  }

  /**
   * Log debug messages (only in development)
   */
  debug(message: string, ...args: any[]): void {
    if (currentLogLevel >= LOG_LEVELS.DEBUG) {
      const sanitizedArgs = args.map(arg => sanitizeData(arg));
      console.log(formatMessage('DEBUG', message, this.context), ...sanitizedArgs);
    }
  }

  /**
   * Log authentication events (special handling)
   */
  auth(event: string, details?: Record<string, any>): void {
    if (currentLogLevel >= LOG_LEVELS.INFO) {
      const sanitizedDetails = details ? sanitizeData(details) : {};
      this.info(`Auth: ${event}`, sanitizedDetails);
    }
  }

  /**
   * Log API requests (without sensitive data)
   */
  request(method: string, url: string, status?: number): void {
    if (currentLogLevel >= LOG_LEVELS.DEBUG) {
      // Only log the path, not query parameters that might contain tokens
      const safePath = url.split('?')[0];
      const statusStr = status ? ` (${status})` : '';
      this.debug(`Request: ${method} ${safePath}${statusStr}`);
    }
  }

  /**
   * Log performance metrics
   */
  performance(operation: string, duration: number): void {
    if (currentLogLevel >= LOG_LEVELS.DEBUG) {
      this.debug(`Performance: ${operation} took ${duration}ms`);
    }
  }
}

// Create default logger instance
const logger = new SecureLogger();

// Export both the class and default instance
export { SecureLogger, logger as default };

// Export specific loggers for different contexts
export const authLogger = SecureLogger.withContext('AUTH');
export const apiLogger = SecureLogger.withContext('API');
export const uiLogger = SecureLogger.withContext('UI');
